import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { registerUser } from '../features/auth/authSlice';
import MinimalHeader from './MinimalHeader';
import MinimalFooter from './MinimalFooter';
import { useFooterInfo } from './FooterInfoContext';
import { API_URL } from '../config';
import './Auth.css';

const YELOOO_DEFAULT_REF = 'YA000001';

const RegistrationPage = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState('');
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [referralError, setReferralError] = useState('');
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successReferralCode, setSuccessReferralCode] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openInfoPanel } = useFooterInfo();
  const authStatus = useSelector((state) => state.auth.status);
  const authError = useSelector((state) => (state.auth.status === 'failed' ? state.auth.error : null));

  const formatAuthError = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.message && typeof err.message === 'string') return err.message;
    if (err.title && typeof err.title === 'string') return err.title;
    return 'Registration failed. Please try again.';
  };

  // Get referral code from URL on mount, or use Yelooo default when none provided
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      validateReferralCode(refCode);
    } else {
      setReferralCode(YELOOO_DEFAULT_REF);
      validateReferralCode(YELOOO_DEFAULT_REF);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code) {
      setReferrerInfo(null);
      setReferralError('');
      return;
    }

    setValidatingReferral(true);
    setReferralError('');

    try {
      const response = await axios.get(`${API_URL}/Referral/validate/${code}`);
      if (response.data.valid) {
        setReferrerInfo(response.data);
        setReferralError('');
      } else {
        setReferrerInfo(null);
        setReferralError(response.data.message || 'Invalid referral code');
      }
    } catch (error) {
      setReferrerInfo(null);
      setReferralError(error.response?.data?.message || 'Invalid referral code');
    } finally {
      setValidatingReferral(false);
    }
  };

  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    if (code.length >= 8) {
      validateReferralCode(code);
    } else {
      setReferrerInfo(null);
      setReferralError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If user entered a referral code, it must be valid; otherwise backend uses Yelooo default
    if (referralCode && !referrerInfo) {
      setReferralError('Please enter a valid referral code');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (!acceptedTerms) {
      setTermsError('Please accept the Terms of Use and Privacy Policy to continue.');
      return;
    }

    const resultAction = await dispatch(registerUser({ 
      username, 
      email, 
      password,
      referralCode: referralCode || YELOOO_DEFAULT_REF,
      acceptTerms: true
    }));
    
    if (registerUser.fulfilled.match(resultAction)) {
      const payload = resultAction.payload;
      const code = payload?.referralCode || payload?.referral_code || '';
      setSuccessReferralCode(code);
      setShowSuccessSnackbar(true);
    }
  };

  const handleSuccessOk = () => {
    setShowSuccessSnackbar(false);
    setSuccessReferralCode('');
    navigate('/login');
  };

  return (
    <div className="auth-page">
      {showSuccessSnackbar && (
        <div className="auth-snackbar-overlay" role="dialog" aria-label="Registration success">
          <div className="auth-snackbar">
            <p className="auth-snackbar-title">Registration successful!</p>
            <p className="auth-snackbar-message">
              Your User Id (Referral Code) is: <strong>{successReferralCode}</strong>. Use this to log in.
            </p>
            <p className="auth-snackbar-note">A welcome email has been sent to your registered email address.</p>
            <button type="button" className="auth-snackbar-ok" onClick={handleSuccessOk}>OK</button>
          </div>
        </div>
      )}
      <MinimalHeader />
      <div className="auth-container">
        <h2>Register</h2>
      
      {/* Referral Info Banner */}
      {referrerInfo && (
        <div className="referral-info-banner">
          <div className="referral-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
          <div className="referral-details">
            <span className="referral-label">You're being referred by</span>
            <span className="referrer-name">{referrerInfo.referrerName}</span>            
          </div>
        </div>
      )}

      {!searchParams.get('ref') && (
        <div className="referral-default-info">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
          <span>Don&apos;t have a referral code? Yelooo&apos;s default referral will be used.</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="referralCode">Referral Code</label>
          <input
            type="text"
            id="referralCode"
            value={referralCode}
            onChange={handleReferralCodeChange}
            placeholder="Enter referral code or leave as Yelooo default"
            className={referralError ? 'input-error' : referrerInfo ? 'input-success' : ''}
          />
          {validatingReferral && <span className="validating">Validating...</span>}
          {referralError && <span className="field-error">{referralError}</span>}
          {referrerInfo && !validatingReferral && (
            <span className="field-success">✓ Valid referral from {referrerInfo.referrerName}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="username">Full Name (as per Aadhar card): *</label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter full name as per Aadhar card"
            autoComplete="name"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email: *</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="password">Password: *</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password: *</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
        </div>

        <div className={`form-group terms-consent ${termsError ? 'has-error' : ''}`}>
          <div className="terms-consent-label">
            <input
              type="checkbox"
              id="acceptedTerms"
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                if (e.target.checked) setTermsError('');
              }}
              required
            />
            <span>
              I have read and agree to the{' '}
              <button
                type="button"
                className="terms-consent-link"
                onClick={() => openInfoPanel('/terms')}
              >
                Terms of Use
              </button>
              {' '}and{' '}
              <button
                type="button"
                className="terms-consent-link"
                onClick={() => openInfoPanel('/privacy')}
              >
                Privacy Policy
              </button>
              . *
            </span>
          </div>
          {termsError && <span className="field-error">{termsError}</span>}
        </div>

        <button 
          type="submit" 
          disabled={authStatus === 'loading' || (referralCode && !referrerInfo) || validatingReferral}
        >
          {authStatus === 'loading' ? 'Registering...' : 'Register'}
        </button>
        
        {authStatus === 'failed' && <p className="error-message">{formatAuthError(authError)}</p>}
        
        <p className="auth-link">
          Already have an account? <span onClick={() => navigate('/login')}>Login here</span>
        </p>
      </form>
      </div>
      <MinimalFooter />
    </div>
  );
};

export default RegistrationPage;
